import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { CategoryNameAndRoleFields } from "../createCategory/CategoryNameAndRoleFields";
import { categoryNameAndRoleSchema } from "../createCategory/useCreateCategoryForm";
import {
  DEFAULT_CREATE_CATEGORY_ROLE,
  PLANT_PROFILE_ROLE_OPTIONS,
  type PlantProfileRoleOption,
} from "portal/utils/categoryCollection/category";
import { Form, Formik } from "formik";
import { object } from "yup";
import {
  Role,
  roleFromJSON,
  roleToJSON,
} from "protos/robot_syncer/plant_profile";
import { useTranslation } from "react-i18next";
import React, { FC, useMemo } from "react";

type EditCategoryFormValues = {
  name: string;
  role: PlantProfileRoleOption;
};

interface EditCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  categoryName: string;
  role: Role;
  onSave: (name: string, role: Role) => void;
}

export const EditCategoryDialog: FC<EditCategoryDialogProps> = ({
  open,
  onClose,
  categoryName,
  role,
  onSave,
}) => {
  const { t } = useTranslation();

  const initialValues: EditCategoryFormValues = useMemo(() => {
    const roleOption = PLANT_PROFILE_ROLE_OPTIONS.includes(roleToJSON(role))
      ? roleToJSON(role)
      : DEFAULT_CREATE_CATEGORY_ROLE;
    return {
      name: categoryName,
      role: roleOption,
    };
  }, [categoryName, role]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("components.categoryCollectionProfile.actions.editNameAndType")}
      </DialogTitle>
      {open && (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={object(categoryNameAndRoleSchema(t))}
          onSubmit={({ name, role: nextRole }) => {
            onSave(name.trim(), roleFromJSON(nextRole));
            onClose();
          }}
        >
          {({
            values,
            submitForm,
            isSubmitting,
            dirty,
            isValid,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form>
              <DialogContent>
                <div className="flex flex-col gap-4">
                  <CategoryNameAndRoleFields
                    role={values.role}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  color="inherit"
                  disabled={isSubmitting}
                  onClick={onClose}
                >
                  {t("utils.actions.cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  disabled={!dirty || !isValid}
                  onClick={submitForm}
                >
                  {t("utils.actions.save")}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};
